import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import moment from 'moment-timezone';
import _ from 'lodash';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import socketInstance from 'websockets/socket';
import MiscUtils from 'services/MiscUtils';
import { useSafePersistedState } from './usePersistedState';

export const READ_RECEIPT_CMD = '/cmd read-receipt';
export const FILE_CMD = '/cmd file';

/**
 * @returns {any}
 */
function useChat(userId) {
  const dispatch = useAppDispatch();
  const authentication = useAppSelector((state) => state.authentication);
  const { activeUserId } = authentication;
  const socketConnection = useAppSelector((state) => state.resource.socketConnection);
  const reduxChatRooms = useAppSelector((state) => state.chat.chatRooms);

  const [joinedRooms, setJoinedRooms] = useState([]);
  const [messages, setMessages] = useState({});
  // loadRooms runs async and can be triggered while activeUserId is a linked dependent. The
  // master-scoped getOwnChatRooms result must NOT wipe dependent-owned rooms from joinedRooms — we
  // read the latest reduxChatRooms via a ref to merge them back in.
  const reduxChatRoomsRef = useRef(reduxChatRooms);
  useEffect(() => {
    reduxChatRoomsRef.current = reduxChatRooms;
  }, [reduxChatRooms]);
  // Ephemeral "Carey is doing X…" status, keyed by chatId. Set on a
  // carey:tool-activity event, cleared when Carey's reply arrives.
  const [careyActivity, setCareyActivity] = useState({});
  const [finishedJoin, setFinishedJoin] = useState(false);
  const [connecting, setConnecting] = useState(false);

  let id = activeUserId || userId;

  const [lastReadTimestamps, setLastReadTimestamps] = useSafePersistedState(
    `chat.lastRead.${id}`,
    {},
  );

  // Sync new rooms from Redux into joinedRooms (e.g. after createOnDemandChatRoom)
  useEffect(() => {
    if (reduxChatRooms.length === 0) return;
    setJoinedRooms((prev) => {
      let updated = prev;
      for (const room of reduxChatRooms) {
        const isOwnRoom = room.staff_id === id || room.patient_id === id;
        if (isOwnRoom && !updated.some((r) => r.id === room.id)) {
          updated = updated === prev ? [...prev, room] : [...updated, room];
        }
      }
      return updated;
    });
  }, [reduxChatRooms, id]);

  const unreadMessages = useMemo(() => {
    let result = {};
    Object.keys(messages).forEach((roomId) => {
      let roomMessages = messages[roomId] || [];
      let lastRead = lastReadTimestamps[roomId] || 0;
      // Own messages act as implicit read receipts
      let lastOwnMessageTime = 0;
      for (let i = roomMessages.length - 1; i >= 0; i--) {
        if (roomMessages[i].author === id) {
          lastOwnMessageTime = roomMessages[i].time;
          break;
        }
      }
      let readCutoff = Math.max(lastRead, lastOwnMessageTime);
      let unread = roomMessages.filter(({ author, time }) => author !== id && time > readCutoff);
      result[roomId] = unread.length;
    });
    return result;
  }, [messages, id, lastReadTimestamps]);

  const unreadCount = useMemo(() => {
    if (!finishedJoin) return 0;
    return Object.values(unreadMessages).reduce((accum, current = 0) => accum + current, 0);
  }, [unreadMessages, finishedJoin]);

  // Seed per-room read markers from the BE so unread counts survive logout (which wipes the
  // localforage-backed `lastReadTimestamps`). Markers are keyed by roomId with a ~30 day TTL on the
  // server. We merge (max) with whatever's already in local state so a more-recent local read isn't
  // clobbered by a stale server value, and vice versa. The authoritative aggregate count is owned by
  // the BE (getUnreadCount / countUnreadHealthRecords); this local marker map only drives the
  // responsive per-room badges in the chat list, which stay coherent with the BE because both derive
  // from the same markers + formula.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      const result = await dispatch(api.Chat.getChatLastRead({ userId: id }));
      if (!cancelled && result?.rooms) {
        setLastReadTimestamps((prev) => {
          const next = { ...prev };
          result.rooms.forEach(({ room_id, time }) => {
            if (room_id && time != null) {
              next[room_id] = Math.max(next[room_id] || 0, time);
            }
          });
          return next;
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // `setLastReadTimestamps` is intentionally omitted: useSafePersistedState returns a fresh setter
    // every render, so including it would re-run this effect (and re-hit the endpoint) on every
    // render. We only want to seed when the active user `id` changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  useEffect(() => {
    if (id) {
      loadRooms();
    }
  }, [dispatch, id]);

  useEffect(() => {
    const socket = socketInstance.getSocket();
    if (!socket) return;

    function onChatMessage(data) {
      const { chatId, message, author, time, attributes, id: messageId } = data;
      console.log('[useChat] onChatMessage received', {
        chatId,
        author,
        time,
        message: message?.substring?.(0, 30),
      });
      if (!chatId || !message || message === READ_RECEIPT_CMD) return;
      if (author === id) return;

      // Carey's reply landed — clear any pending "doing X…" status for this room.
      setCareyActivity((prev) => (prev[chatId] ? { ...prev, [chatId]: null } : prev));

      setMessages((prevState) => {
        const updated = {
          ...prevState,
          // Preserve `attributes` (e.g. author_role: 'carey', tool_name) and `id` so Carey-authored
          // messages render distinctly. Without this they're dropped on live delivery and only
          // survive on archive-loaded messages.
          [chatId]: [
            ...(prevState[chatId] || []),
            { author, message, time, attributes, id: messageId },
          ],
        };
        console.log('[useChat] setMessages called, room message count:', updated[chatId]?.length);
        return updated;
      });
    }

    function onRoomUpdate(data) {
      const { room } = data;
      if (!room) return;
      if (!room.is_active) {
        setJoinedRooms((prev) => prev.filter((r) => r.id !== room.id));
        return;
      }
      const isOwnRoom = room.staff_id === id || room.patient_id === id;
      if (!isOwnRoom) return;
      setJoinedRooms((prev) => {
        const exists = prev.some((r) => r.id === room.id);
        return exists ? prev.map((r) => (r.id === room.id ? room : r)) : [...prev, room];
      });
      // A room can arrive here with history we've never loaded — an admin
      // reopening an archived chat pushes a room the client has no messages for,
      // and it would render empty until the next loadRooms (remount, reconnect,
      // or return-from-background). Seed from the room payload, but only when we
      // have nothing: rooms we're already tracking have live message state that
      // this event's snapshot must never clobber.
      setMessages((prev) => (prev[room.id] ? prev : { ...prev, [room.id]: room.messages || [] }));
    }

    function onRoomArchived(data) {
      const { room } = data;
      if (!room) return;
      setJoinedRooms((prev) => prev.filter((r) => r.id !== room.id));
    }

    function onCareyToolActivity(data) {
      const { chatId, label } = data || {};
      if (!chatId || !label) return;
      setCareyActivity((prev) => ({ ...prev, [chatId]: label }));
    }

    socket.on('chat:message', onChatMessage);
    socket.on('chat:room-update', onRoomUpdate);
    socket.on('chat:room-archived', onRoomArchived);
    socket.on('carey:tool-activity', onCareyToolActivity);

    return () => {
      socket.off('chat:message', onChatMessage);
      socket.off('chat:room-update', onRoomUpdate);
      socket.off('chat:room-archived', onRoomArchived);
      socket.off('carey:tool-activity', onCareyToolActivity);
    };
  }, [id, socketConnection]);

  async function loadRooms() {
    setConnecting(true);
    let rooms = await dispatch(api.Chat.getOwnChatRooms());
    if (rooms) {
      // getOwnChatRooms is master-scoped (server reads the JWT, no patientId param). When viewing a
      // linked dependent, the API result excludes the dependent's rooms — including a Carey room we
      // just created. Merge in any redux-known rooms that match the current id's ownership so they
      // aren't dropped.
      const apiIds = new Set(rooms.map((r) => r.id));
      const extras = (reduxChatRoomsRef.current || []).filter(
        (r) => !apiIds.has(r.id) && (r.staff_id === id || r.patient_id === id),
      );
      setJoinedRooms([...rooms, ...extras]);

      setMessages((prev) => {
        const next = { ...prev };
        rooms.forEach((room) => {
          next[room.id] = room.messages || [];
        });
        // Keep messages already in state for `extras` (e.g. a freshly created Carey room that the
        // master-scoped API doesn't return) — they aren't in `rooms`, so we just leave them alone.
        return next;
      });
    }
    setFinishedJoin(true);
    setConnecting(false);
  }

  const sendMessage = useCallback(
    async (text, chatId) => {
      const socket = socketInstance.getSocket();
      // The socket object survives a dead connection (socket.io keeps it around while
      // reconnecting), so `if (socket)` isn't enough — emitting then buffers into the void and the
      // ack never fires, leaving a ghost "sending" message. Guard on the real connectivity flag and
      // surface a toast instead of adding an orphan optimistic message. Covers the race where the
      // socket drops between render and click, and the Carey inline-confirm buttons that call
      // sendMessage directly (not gated by the input bar).
      if (!socket || !socket.connected) {
        MiscUtils.showToastrError(
          'Not connected',
          'Your message wasn’t sent — reconnecting. Please try again in a moment.',
        );
        return;
      }

      const time = moment().unix();
      const tempId = `${time}-${Math.random()}`;

      setMessages((prevState) => ({
        ...prevState,
        [chatId]: [
          ...(prevState[chatId] || []),
          { id: tempId, author: id, message: text, time, status: 'sending' },
        ],
      }));

      // Mark as read since user is actively chatting
      setLastReadTimestamps((prev) => ({ ...prev, [chatId]: time }));

      console.log('chat:send EMIT');
      // `actingAs` is the patient whose room we're sending into. When the master is viewing a
      // linked dependent, `id` is the dependent's id (not the authenticated master). The server
      // needs this so it can authorize the send against room.patient_id when the socket's
      // authenticated user is a master acting on behalf of a managed patient.
      socket.emit('chat:send', { chatId, message: text, actingAs: id }, (ack) => {
        console.log('chat:send RCVE', ack);
        setMessages((prevState) => ({
          ...prevState,
          [chatId]: (prevState[chatId] || []).map((msg) =>
            msg.id === tempId ? { ...msg, status: ack?.success ? 'sent' : 'failed' } : msg,
          ),
        }));
      });
    },
    [id],
  );

  const retrySend = useCallback(
    async (failedMsg, chatId) => {
      setMessages((prevState) => ({
        ...prevState,
        [chatId]: (prevState[chatId] || []).filter((msg) => msg.id !== failedMsg.id),
      }));
      await sendMessage(failedMsg.message, chatId);
    },
    [sendMessage],
  );

  // Message timestamps come from the server, so a device clock running behind leaves
  // `message.time > moment().unix()` true even after marking read — ChatWindow's mark-read effect
  // then re-fires on every render and floods PUT /chat/last-read until the clock catches up
  // (observed at ~21 req/s from an iOS device ~7.5 min behind). Two defenses: mark with the max
  // message time rather than wall clock so one call always zeroes the unread count, and skip the
  // call entirely when it wouldn't advance the marker we already wrote.
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  const lastMarkedTimesRef = useRef({});

  const markMessagesRead = useCallback(
    async (chatId) => {
      let latestMessageTime = 0;
      for (const { time: messageTime } of messagesRef.current[chatId] || []) {
        if (messageTime > latestMessageTime) latestMessageTime = messageTime;
      }
      const time = Math.max(moment().unix(), latestMessageTime);
      if ((lastMarkedTimesRef.current[chatId] || 0) >= time) return;
      lastMarkedTimesRef.current[chatId] = time;
      setLastReadTimestamps((prev) => ({ ...prev, [chatId]: time }));
      // Persist the marker server-side so it survives logout / works across devices. Fire-and-forget;
      // the local update above already drives the in-session count.
      dispatch(
        api.Chat.updateChatLastRead({
          userId: id,
          roomId: chatId,
          integerResource: { value: time },
        }),
      );
    },
    // `setLastReadTimestamps` omitted on purpose — useSafePersistedState hands back a new setter every
    // render, so including it would make this callback (and any effect depending on it) churn every
    // render. The closure still calls the latest setter correctly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, id],
  );

  const leaveRoom = useCallback(async (roomName) => {
    setMessages((prevState) => {
      return { ...prevState, [roomName]: [] };
    });
    setJoinedRooms((prevState) => {
      return prevState.filter((room) => room.id !== roomName);
    });
  }, []);

  // Reflect the real socket state so the chat view can hide the input + show the "Not Connected"
  // banner when the connection is dead. `!== 'disconnected'` (rather than `=== 'connected'`) so the
  // cold-start window (socketConnection undefined before the first connect) and the brief
  // 'refreshing' state during token refresh don't flash a false disconnect banner — only the
  // genuine dead-connection state blocks.
  const connected = socketConnection !== 'disconnected';

  // Refetch messages when app returns from background
  useEffect(() => {
    function onVisibilityChange() {
      if (!document.hidden && id) {
        loadRooms();
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [id]);

  const onReconnect = useCallback(async () => {
    return loadRooms();
  }, []);

  return {
    messages,
    careyActivity,
    sendMessage,
    retrySend,
    joinedRooms,
    markMessagesRead,
    unreadMessages,
    leaveRoom,
    unreadCount,
    connected,
    connecting,
    onReconnect,
  };
}

export default useChat;
